import type { B2BEventType } from 'api/requestTypes';
import {
  B2B_EVENT_TYPE_OPTIONS_ORDER,
  B2B_PUBLIC_EVENT_CATEGORY_OPTIONS_ORDER,
} from 'components/b2b/b2bEventChipOptions';
import { INVALID_EMAIL_REGEX } from 'utils/constants';
import { normalizeIraqiPhone } from 'utils/iraqiPhone';
import * as yup from 'yup';

const b2bEventTypeSchema = yup
  .mixed<B2BEventType>()
  .oneOf([...B2B_EVENT_TYPE_OPTIONS_ORDER])
  .required('b2b.registration.field.required');

/**
 * Client fields + contract-aligned shape for {@link mapB2bFormToCreateRequest}.
 * Validates conditionals: `previousEvents` when organized before; `publicEventCategory` for every `eventType`;
 * optional email (format when present); Iraqi phone (E.164 after `FieldPhone` blur).
 */
export const b2bRegistrationSchema = yup.object({
  fullName: yup
    .string()
    .transform((v) => v?.trim() ?? '')
    .required('b2b.registration.field.required')
    .min(2, 'b2b.registration.field.invalid'),

  companyOrOrganization: yup
    .string()
    .transform((v) => {
      const t = v?.trim();
      return t === '' ? undefined : t;
    })
    .optional(),

  phoneNumber: yup
    .string()
    .transform((v) => v?.trim() ?? '')
    .required('b2b.registration.field.required')
    .test('iraqi-e164', 'checkout.phoneNumber.invalid', (val) => {
      if (!val) {
        return false;
      }
      return normalizeIraqiPhone(val).ok;
    }),

  email: yup
    .string()
    .transform((v) => {
      const t = v?.trim();
      return t === '' ? undefined : t;
    })
    .optional()
    .test('email-format', 'b2b.registration.field.invalid', (val) => {
      if (val == null) {
        return true;
      }
      return INVALID_EMAIL_REGEX.test(val);
    }),

  city: yup.string().required('b2b.registration.field.required'),

  websiteOrSocialMedia: yup
    .string()
    .transform((v) => {
      const t = v?.trim();
      return t === '' ? undefined : t;
    })
    .optional()
    .test('url', 'b2b.registration.field.invalid', (val) => {
      if (val == null) {
        return true;
      }
      return yup.string().url().isValidSync(val);
    }),

  hasOrganizedBefore: yup.boolean().required(),
  previousEvents: yup
    .string()
    .default('')
    .when('hasOrganizedBefore', {
      is: true,
      then: (schema) =>
        schema
          .transform((v) => v?.trim() ?? '')
          .required('b2b.registration.field.required')
          .min(2, 'b2b.registration.field.invalid'),
      otherwise: (schema) => schema.optional().transform(() => undefined),
    }),

  eventName: yup
    .string()
    .transform((v) => v?.trim() ?? '')
    .required('b2b.registration.field.required')
    .max(500, 'b2b.registration.field.invalid'),

  description: yup
    .string()
    .transform((v) => v?.trim() ?? '')
    .required('b2b.registration.field.required')
    .min(10, 'b2b.registration.field.invalid'),

  eventDate: yup
    .string()
    .transform((v) => {
      const t = v?.trim();
      return t === '' ? undefined : t;
    })
    .optional(),

  eventTime: yup
    .string()
    .transform((v) => {
      const t = v?.trim();
      return t === '' ? undefined : t;
    })
    .optional(),

  eventVenue: yup
    .string()
    .transform((v) => {
      const t = v?.trim();
      return t === '' ? undefined : t;
    })
    .optional(),

  isVenueConfirmed: yup.boolean().required(),

  eventType: b2bEventTypeSchema,

  publicEventCategory: yup
    .string()
    .required('b2b.registration.field.required')
    .oneOf([...B2B_PUBLIC_EVENT_CATEGORY_OPTIONS_ORDER]),

  termsAccepted: yup.boolean().required().oneOf([true], 'b2b.registration.terms.required'),
});

export type B2bRegistrationFormValues = yup.InferType<typeof b2bRegistrationSchema>;
